import type { SmartPasteBlockCode } from '@/types/smartPaste';

/**
 * Turn an axios failure from POST /orders/smart-paste into something the card
 * can show. Core's contract: 429 carries `{code, resetsAtUtc}` so we can count
 * down to a real retry time; 502/503 mean the parser is down (the merchant
 * should just type the order in); everything else falls back to the message.
 */
export type SmartPasteError =
  | { kind: 'rate_limited'; retryInSeconds: number }
  | { kind: 'unavailable' }
  | { kind: 'empty' }
  | { kind: 'too_long'; length: number }
  | { kind: 'blocked'; reasons: string[]; code?: SmartPasteBlockCode | null; warnings?: string[] }
  | { kind: 'generic'; message?: string };

/** Cap so a bad/stale clock can never lock the button for hours. */
const MAX_RETRY_SECONDS = 300;

export function readSmartPasteError(error: unknown, now: number = Date.now()): SmartPasteError {
  const response = (error as { response?: { status?: number; data?: any } })?.response;
  const status = response?.status;
  const data = response?.data;

  if (status === 429) {
    const resetsAt = data?.object?.resetsAtUtc;
    const parsed = resetsAt ? Date.parse(resetsAt) : NaN;
    const seconds = Number.isNaN(parsed) ? 10 : Math.ceil((parsed - now) / 1000);
    return {
      kind: 'rate_limited',
      retryInSeconds: Math.min(MAX_RETRY_SECONDS, Math.max(1, seconds)),
    };
  }

  if (status === 502 || status === 503) return { kind: 'unavailable' };

  return { kind: 'generic', message: data?.message };
}
